import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { useReactToPrint } from "react-to-print"
import * as XLSX from "xlsx"
import Table from "../../../core/common/dataTable/index"
import PredefinedDateRanges from "../../../core/common/datePicker"
import TooltipOption from "../../../core/common/tooltipOption"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"
import { resolveByGrNumbers, type ResolvedStudent } from "../../../service/student-status"
import {
  listStudentLeaves,
  createStudentLeave,
  cancelStudentLeave,
  type StudentLeave,
} from "../../../service/student-leave"

const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString("en-GB") : "—")

const COPY = {
  leave: { noun: "Leave", verb: "leave", verbGerund: "requesting leave" },
  suspension: { noun: "Suspension", verb: "suspension", verbGerund: "being suspended" },
}

export type StudentLeavePanelHandle = {
  refresh: () => void
  print: () => void
  exportExcel: () => void
}

type Props = {
  type: "leave" | "suspension"
  // The standalone Suspension page has no page-level toolbar of its own, so
  // the panel supplies its own Add button + refresh/print/export. Leave
  // Management already has that toolbar in its page header (shared with the
  // Employees tab) and drives this panel through the ref instead, so it sets
  // this false to avoid showing the same controls twice.
  showOwnToolbar?: boolean
}

// The record-directly-against-a-date-range mechanic — no request/approve
// step, unlike the Employees side of Leave Management. Used for BOTH the
// standalone Student Suspensions page and the Students tab on Leave
// Management: same StudentLeave backend, same GR-add/paste-then-shared-date-
// range-and-reason flow, only the `type` (and the words on screen) differ.
// One student or several go through the same modal — there is no separate
// "bulk" flow to maintain.
const StudentLeavePanel = forwardRef<StudentLeavePanelHandle, Props>(
  ({ type, showOwnToolbar = true }, ref) => {
    const copy = COPY[type]
    const currentBranch = useSelector(selectCurrentBranch)
    const dismissRef = useRef<HTMLButtonElement>(null)
    const printRef = useRef<HTMLDivElement>(null)
    const modalId = `add_${type}_modal`

    const [rows, setRows] = useState<StudentLeave[]>([])
    const [rowsLoading, setRowsLoading] = useState(false)

    // Register toolbar — mirrors the Employees tab's Leave List card as
    // closely as the data allows: a date range (against fromDate), a status
    // filter, a sort order and a search box. All client-side — a campus'
    // whole leave/suspension history is small enough that fetching it once
    // and filtering in place is simpler than a paginated, server-filtered
    // endpoint for what is a lightly-used record, not a high-volume list.
    const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null)
    const [statusFilter, setStatusFilter] = useState<"all" | "active" | "cancelled">("all")
    const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc")
    const [searchText, setSearchText] = useState("")
    const filterDropdownRef = useRef<HTMLDivElement | null>(null)

    const [grText, setGrText] = useState("")
    const [resolving, setResolving] = useState(false)
    const [selected, setSelected] = useState<Map<string, ResolvedStudent>>(new Map())

    const today = new Date().toISOString().slice(0, 10)
    const [fromDate, setFromDate] = useState(today)
    const [toDate, setToDate] = useState(today)
    const [reason, setReason] = useState("")
    const [saving, setSaving] = useState(false)

    const loadRegister = async () => {
      setRowsLoading(true)
      try {
        const r = await listStudentLeaves({ branchId: currentBranch?._id, type })
        setRows(r.data || [])
      } catch (e: any) {
        toast.error(e?.response?.data?.message || `Could not load ${copy.verb} records`)
      } finally {
        setRowsLoading(false)
      }
    }

    useEffect(() => {
      loadRegister()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBranch?._id, type])

    const displayRows = useMemo(() => {
      let list = rows
      if (dateRange) {
        const start = new Date(dateRange.start).getTime()
        const end = new Date(dateRange.end).getTime()
        list = list.filter((r) => {
          const t = new Date(r.fromDate).getTime()
          return t >= start && t <= end
        })
      }
      if (statusFilter !== "all") {
        list = list.filter((r) => r.status === statusFilter)
      }
      if (searchText.trim()) {
        const q = searchText.trim().toLowerCase()
        list = list.filter(
          (r) => r.grNumber?.toLowerCase().includes(q) || r.studentName?.toLowerCase().includes(q)
        )
      }
      const sorted = [...list].sort((a, b) => {
        const diff = new Date(a.fromDate).getTime() - new Date(b.fromDate).getTime()
        return sortOrder === "desc" ? -diff : diff
      })
      return sorted
    }, [rows, dateRange, statusFilter, searchText, sortOrder])

    const handlePrint = useReactToPrint({ contentRef: printRef })

    const handleExportExcel = () => {
      const ws = XLSX.utils.json_to_sheet(
        displayRows.map((r) => ({
          GR: r.grNumber || "",
          Name: r.studentName || "",
          From: fmt(r.fromDate),
          To: fmt(r.toDate),
          "Days marked": r.daysMarked,
          Reason: r.reason || "",
          Status: r.status === "active" ? "Active" : "Cancelled",
        }))
      )
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, `${copy.noun}s`)
      XLSX.writeFile(wb, `student-${copy.verb}s.xlsx`)
    }

    useImperativeHandle(ref, () => ({
      refresh: loadRegister,
      print: handlePrint,
      exportExcel: handleExportExcel,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [displayRows])

    const resetModal = () => {
      setGrText("")
      setSelected(new Map())
      setFromDate(today)
      setToDate(today)
      setReason("")
    }

    const removeFromSelection = (studentId: string) => {
      setSelected((prev) => {
        const next = new Map(prev)
        next.delete(studentId)
        return next
      })
    }

    // Splits on commas, spaces or newlines — one GR typed in, or a whole list
    // pasted off an export, go through the same box.
    const addFromGrText = async () => {
      const grNumbers = Array.from(new Set(
        grText.split(/[\s,]+/).map((g) => g.trim()).filter(Boolean)
      ))
      if (!grNumbers.length) return
      setResolving(true)
      try {
        const r = await resolveByGrNumbers(grNumbers)
        setSelected((prev) => {
          const next = new Map(prev)
          r.found.forEach((s) => next.set(s.studentId, s))
          return next
        })
        if (r.notFound.length) {
          toast.warning(`Not found: ${r.notFound.join(", ")}`)
        }
        setGrText("")
      } catch (e: any) {
        toast.error(e?.response?.data?.message || "Could not look those up")
      } finally {
        setResolving(false)
      }
    }

    const eligible = Array.from(selected.values()).filter((s) => s.attendanceEligible)
    const canSubmit = !saving && eligible.length > 0 && !!fromDate && !!toDate && reason.trim().length > 0

    const submit = async () => {
      if (!canSubmit) return
      setSaving(true)
      let succeeded = 0
      const failed: { name: string; message: string }[] = []
      for (const s of eligible) {
        try {
          await createStudentLeave({
            studentId: s.studentId,
            type,
            fromDate,
            toDate,
            reason: reason.trim(),
          })
          succeeded += 1
        } catch (e: any) {
          failed.push({ name: s.name || s.grNumber, message: e?.response?.data?.message || "failed" })
        }
      }
      setSaving(false)
      if (failed.length) {
        toast.warning(
          `${succeeded} recorded — ${failed.map((f) => `${f.name}: ${f.message}`).join("; ")}`
        )
      } else {
        toast.success(`${copy.noun} recorded for ${succeeded} student${succeeded === 1 ? "" : "s"}`)
      }
      resetModal()
      loadRegister()
      if (!failed.length) dismissRef.current?.click()
    }

    const cancelOne = async (leaveId: string) => {
      try {
        await cancelStudentLeave(leaveId)
        toast.success(`${copy.noun} cancelled — those attendance marks were removed`)
        loadRegister()
      } catch (e: any) {
        toast.error(e?.response?.data?.message || "Could not cancel")
      }
    }

    const columns = [
      { title: "GR", dataIndex: "grNumber", sorter: (a: StudentLeave, b: StudentLeave) => (a.grNumber || "").localeCompare(b.grNumber || "") },
      { title: "Name", dataIndex: "studentName", sorter: (a: StudentLeave, b: StudentLeave) => (a.studentName || "").localeCompare(b.studentName || "") },
      { title: "From", dataIndex: "fromDate", render: (d: string) => fmt(d), sorter: (a: StudentLeave, b: StudentLeave) => new Date(a.fromDate).getTime() - new Date(b.fromDate).getTime() },
      { title: "To", dataIndex: "toDate", render: (d: string) => fmt(d) },
      { title: "Days marked", dataIndex: "daysMarked" },
      {
        title: "Reason",
        dataIndex: "reason",
        render: (reason: string) => (
          <span title={reason}>{reason && reason.length > 50 ? `${reason.substring(0, 50)}...` : reason || "—"}</span>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        render: (status: string) =>
          status === "active" ? (
            <span className="badge badge-soft-warning">Active</span>
          ) : (
            <span className="badge badge-soft-secondary">Cancelled</span>
          ),
      },
      {
        title: "Action",
        dataIndex: "action",
        render: (_: any, record: StudentLeave) =>
          record.status === "active" && (
            <button className="btn btn-sm btn-outline-danger" onClick={() => cancelOne(record._id)}>
              Cancel
            </button>
          ),
      },
    ]

    return (
      <>
        {showOwnToolbar && (
          <div className="d-flex justify-content-end align-items-center flex-wrap mb-3">
            <TooltipOption onRefresh={async () => loadRegister()} onExportPdf={handlePrint} onExportExcel={handleExportExcel} />
            <button className="btn btn-primary" data-bs-toggle="modal" data-bs-target={`#${modalId}`}>
              <i className="ti ti-square-rounded-plus me-2" />
              Add {copy.noun}
            </button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{copy.noun}s</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="mb-3 me-2">
                <PredefinedDateRanges onDateSelect={({ startDate, endDate }) => setDateRange({ start: startDate, end: endDate })} />
              </div>
              <div className="dropdown mb-3 me-2">
                <button
                  type="button"
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                >
                  <i className="ti ti-filter me-2" />
                  Filter
                </button>
                <div className="dropdown-menu drop-width" ref={filterDropdownRef}>
                  <div className="d-flex align-items-center border-bottom p-3">
                    <h4>Filter</h4>
                  </div>
                  <div className="p-3 border-bottom">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as any)}
                    >
                      <option value="all">All</option>
                      <option value="active">Active</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="p-3 d-flex align-items-center justify-content-end">
                    <button
                      type="button"
                      className="btn btn-light me-3"
                      onClick={() => setStatusFilter("all")}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
              <div className="dropdown mb-3">
                <button
                  type="button"
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  <i className="ti ti-sort-ascending-2 me-2" />
                  Sort by {sortOrder === "desc" ? "Newest First" : "Oldest First"}
                </button>
                <ul className="dropdown-menu p-3">
                  <li>
                    <div className="dropdown-item rounded-1" style={{ cursor: "pointer" }} onClick={() => setSortOrder("desc")}>
                      Newest First
                    </div>
                  </li>
                  <li>
                    <div className="dropdown-item rounded-1" style={{ cursor: "pointer" }} onClick={() => setSortOrder("asc")}>
                      Oldest First
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="card-body p-0 py-3 printable-content" ref={printRef}>
            <div className="print-only-header">
              <div className="school-brand">
                <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
              </div>
              <div className="report-title">Student {copy.noun}s</div>
              <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
            </div>
            <Table
              columns={columns}
              dataSource={displayRows}
              Selection={false}
              loading={rowsLoading}
              setSearchText={setSearchText}
            />
          </div>
        </div>

        {/* One student or several, same form either way. */}
        <div className="modal fade" id={modalId}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Add {copy.noun}</h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  onClick={resetModal}
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                <label className="form-label">GR number(s) — comma, space or one per line</label>
                <div className="d-flex gap-2 flex-wrap align-items-start mb-2">
                  <textarea
                    className="form-control"
                    rows={2}
                    value={grText}
                    onChange={(e) => setGrText(e.target.value)}
                    placeholder={"e.g.\n135226\n135227, 135228"}
                  />
                  <button
                    className="btn btn-outline-secondary"
                    disabled={resolving || !grText.trim()}
                    onClick={addFromGrText}
                  >
                    {resolving ? "Looking up…" : "Add"}
                  </button>
                </div>

                {selected.size > 0 && (
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {Array.from(selected.values()).map((s) => (
                      <span
                        key={s.studentId}
                        className={`d-flex align-items-center gap-1 py-1 px-2 border rounded ${
                          !s.attendanceEligible ? "border-danger" : ""
                        }`}
                      >
                        <span className="small">{s.name || s.grNumber} · GR {s.grNumber}</span>
                        {s.attendanceEligible ? (
                          <span className="badge badge-soft-success">{s.statusLabel}</span>
                        ) : (
                          <span className="badge badge-soft-danger" title="Attendance is not marked for this status — will be skipped">
                            {s.statusLabel} — can't add
                          </span>
                        )}
                        <button
                          type="button"
                          className="btn-close ms-1"
                          style={{ fontSize: 8 }}
                          onClick={() => removeFromSelection(s.studentId)}
                          aria-label="Remove"
                        />
                      </span>
                    ))}
                  </div>
                )}

                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">From</label>
                    <input
                      type="date"
                      className="form-control"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">To</label>
                    <input
                      type="date"
                      className="form-control"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">
                      Reason <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={`Why are these students ${copy.verbGerund}?`}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button ref={dismissRef} type="button" className="d-none" data-bs-dismiss="modal" />
                <button type="button" className="btn btn-light me-2" data-bs-dismiss="modal" onClick={resetModal}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" disabled={!canSubmit} onClick={submit}>
                  {saving ? "Saving…" : `Record ${copy.verb} (${eligible.length || selected.size})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }
)

StudentLeavePanel.displayName = "StudentLeavePanel"

export default StudentLeavePanel
